import { IMedia } from "api/types/_media";
import { Option } from "api/types/_public";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownMedia from "components/Common/DropdownMedia";
import DropdownOption from "components/Common/DropdownOption";
import LabelWrapper from "components/Common/LabelWrapper";
import TableContainer from "components/Common/TableContainer";
import { useRole } from "components/Hooks/UserHooks";
import SVGImageDefault from "components/Images/SVGImageDefault";
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { ARR_INDEX_TEXT_DAY_OF_WEEK, DEVICE_OPTIONS } from "helpers/constans";
import { formatLinkHTTPS, formatNumberWithCommas } from "helpers/format";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import { createSelector } from "reselect";
import { getAllMedias } from "store/media/thunk";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import { reportAgencies as onGetReportAgencies } from "../../../store/thunks";
import ChartLine from "./ChartLine";


const SORT_BY_DEFAULT = 'date';
const ORDER_BY_DEFAULT = 'DESC';

registerLocale('en', en);
registerLocale('ko', ko);

const ReportAgency = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();

    const DEVICE_OPTIONS_LANG = DEVICE_OPTIONS?.map((item: any) => ({ value: item?.value, label: t(item?.label) })) || [];

    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 50),
        sort_by: withDefault(StringParam, SORT_BY_DEFAULT),
        order_by: withDefault(StringParam, ORDER_BY_DEFAULT),
        start_date: withDefault(StringParam, moment().subtract(30, 'days').format("Y-MM-DD")),
        end_date: withDefault(StringParam, moment().format("Y-MM-DD")),
        media_id: withDefault(StringParam, ''),
        device: withDefault(StringParam, ''),
    });

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.Report;

    const ManagementProperties = createSelector(
        selectLayoutState,
        (state) => ({
            agencies: state.agencies,
            isAgencySuccess: state.isAgencySuccess,
            isAgencyLoading: state.isAgencyLoading,
            error: state.error,
        })
    );

    const { agencies, isAgencyLoading } = useSelector(ManagementProperties);

    const [dateSearch, setDateSearch] = useState<any[]>([moment(query?.start_date || "", 'Y-MM-DD').toDate(), moment(query?.end_date || "", 'Y-MM-DD').toDate()]);
    const [startDate, endDate] = dateSearch;

    const [deviceSearch, setDeviceSearch] = useState<Option | null>(DEVICE_OPTIONS_LANG?.filter((item) => String(item?.value) === String(query?.device))[0]);

    const [listMedia, setListMedia] = useState<IMedia[]>([]);

    const [mediaSearch, setMediaSearch] = useState<Option | null>({
        label: listMedia?.filter((item) => String(item?.id || '') === String(query?.media_id || ''))[0]?.name || t('All Media'),
        value: `${listMedia?.filter((item) => String(item?.id || '') === String(query?.media_id || ''))[0]?.id || ''}`
    });

    useEffect(() => {
        dispatch(onGetReportAgencies(query));
    }, [JSON.stringify(query)]);

    const searchData = () => {
        setQuery({
            ...query,
            page: 1,
            start_date: startDate ? moment(new Date(startDate)).format("Y-MM-DD") : '',
            end_date: endDate ? moment(new Date(endDate)).format("Y-MM-DD") : '',
            media_id: mediaSearch?.value || '',
            device: deviceSearch?.value || '',
            time_request: + new Date()
        });
    };

    const resetData = () => {
        const queryNew = {
            ...query,
            start_date: moment().subtract(30, 'days').format("Y-MM-DD"),
            end_date: moment().format("Y-MM-DD"),
            sort_by: SORT_BY_DEFAULT,
            order_by: ORDER_BY_DEFAULT,
            device: '',
            media_id: '',
            time_request: + new Date(),
        };
        setQuery(queryNew, "push");
        setMediaSearch({ label: t('All Media'), value: '' });
        setDeviceSearch(null);
        setDateSearch([moment(queryNew?.start_date || "", 'Y-MM-DD').toDate(), moment(queryNew?.end_date || "", 'Y-MM-DD').toDate()]);
    };

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t('Date'),
                accessor: "date",
                filterable: true,
                sortable: true,
                thWidth: 130,
                thClass: 'text-start align-middle',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const arrDate = String(cell?.value || '').split(' ');
                    const num = moment(cell?.value).days(); // 0 is Sunday , 1 is Monday, ... , 6 is Saturday
                    const classText = num === 0 ? 'text-danger row-bg-danger' : (num === 6 ? 'text-primary row-bg-primary' : '');
                    const textNote = t(`STANDS_${ARR_INDEX_TEXT_DAY_OF_WEEK[num]}`);
                    return (
                        !!item?.text_row ? <strong>{item?.text_row}</strong> : <div className="text-start" style={{ minWidth: '130px' }}>
                            <span className={classText}>{moment(arrDate?.[0] || '', 'Y-MM-DD').format("MM-DD")} ({textNote})</span>
                        </div>
                    )
                },
            },
            // {
            //     Header: t('Media'),
            //     accessor: "media",
            //     filterable: true,
            //     sortable: false,
            //     thWidth: 200,
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         const media = listMedia?.find((itm: any) => String(itm?.id) === String(item?.media?.id)) || null;
            //         return (<>
            //             {!!item?.text_row ? <strong>{item?.text_row}</strong> : (<>{!!media && (<div className="d-flex align-items-center" style={{ minWidth: '160px' }}>
            //                 <div className="me-2">
            //                     {media?.logo_url ? (
            //                         <img src={media?.logo_url || ''} loading="lazy" alt={media?.name} className="avatar-sm object-fit-contain" style={{ backgroundColor: 'rgb(234 234 234 / 49%)', borderRadius: '5px' }} />) : (
            //                         <SVGImageDefault style={{ height: '48px' }} />
            //                     )}
            //                 </div>
            //                 <div>
            //                     <div>
            //                         {media?.name || ''}
            //                     </div>
            //                     <Link className='text-normal flex-shrink-0 ' to={formatLinkHTTPS(media?.website)} target="_blank">
            //                         {media?.website || ''}
            //                     </Link>
            //                 </div>
            //             </div>)}</>)}
            //         </>)
            //     },
            // },
            // {
            //     Header: t('Device'),
            //     accessor: "device",
            //     filterable: true,
            //     sortable: false,
            //     thWidth: 70,
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 {!!item?.text_row ? <strong>{item?.text_row}</strong> : <div style={{ minWidth: '70px' }}>{DEVICE_OPTIONS_LANG?.filter((item) => (!!cell?.value && String(item?.value) === String(cell?.value)))[0]?.label || cell?.value}</div>}
            //             </>
            //         )
            //     },
            // },
            {
                Header: t('Impression'),
                accessor: "impression",
                filterable: false,
                sortable: true,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            // {
            //     Header: t('Impression (Bot)'),
            //     accessor: "impression_bot",
            //     filterable: false,
            //     sortable: true,
            //     thClass: 'align-middle text-end',
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
            //             </>
            //         )
            //     },
            // },
            {
                Header: t('Media Revenue'),
                accessor: "media_revenue",
                filterable: false,
                sortable: true,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Agency Revenue'),
                accessor: "agency_revenue",
                filterable: false,
                sortable: true,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Total Revenue'),
                accessor: "total_revenue",
                filterable: false,
                sortable: true,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
        ],
        [i18n?.language, userPermissions, JSON.stringify(listMedia)]
    );

    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 })
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return ({ ..._prev, ...sortBy });
        });
    }, []);

    const handleChangePicker = (values: any[] = []) => {
        setDateSearch((_prev: any) => values);
    }

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                const [resMediaLog]: any = await Promise.all([getAllMedias()]);
                const list = ([{ name: t('All Media'), id: '', website: '' } as any]).concat(resMediaLog?.data || []);
                const media = list?.find((item) => String(item?.id || '') === String(query?.media_id || '')) || null;
                setListMedia((_prev) => list);
                setMediaSearch((_prev) => ({
                    label: media?.name || t('All Media'),
                    value: media?.id || '',
                    logo_url: media?.logo_url || ''
                }));
            } catch (error: any) {
                return error;
            }
        };
        handleCallAllOption();
    }, []);

    useEffect(() => {
        if (deviceSearch) {
            setDeviceSearch((_prev: any) =>
                DEVICE_OPTIONS_LANG?.filter((e: any) => e.value === deviceSearch?.value)[0]
            );
        }
        if (mediaSearch) {
            setListMedia((_prev) => (_prev || [])?.map((item) => item?.id ? item : ({ ...item, name: t('All Media') })));
            (!mediaSearch?.value) && setMediaSearch((prev) => ({ label: t('All Media'), value: '' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    useEffect(() => {
        document.title = `${t("Daily Report")} | AdsHub`;
        document.body.classList.remove("vertical-sidebar-enable");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);


    const categories = useMemo(() => {
        return agencies?.list?.map((item: any) => item?.date).reverse() || [];
    }, [agencies?.list, i18n?.language]);

    const series = useMemo(() => {
        const obChart = agencies?.list?.reduce((ob: any, item: any) => {
            ob.impression?.push(Number((item?.impression).replace(/,/g, '')));
            ob.revenue?.push(Number((item?.agency_revenue).replace(/,/g, '')));
            return ob;
        }, { impression: [], revenue: [] }) || [];
        return (
            [
                {
                    name: t('Impression'),
                    type: 'line',
                    data: obChart?.impression?.reverse(),
                },
                {
                    name: t('Revenue'),
                    type: 'column',
                    data: obChart?.revenue?.reverse(),
                },

            ]
        )
    }, [agencies?.list, i18n?.language]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('Daily Report')} />
                    <Row>
                        <Col sm={12} md={12}>
                            <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }} className="ribbon-box right">
                                <div className="ribbon round-shape" style={{ background: 'rgb(40 99 213)', fontWeight: 300, fontSize: '0.8rem', top: '11px' }}>{t('This data is analyzed per 10 mins')}</div>
                                <CardHeader className="border-0" style={{ borderRadius: '30px' }}>
                                    <Row className="g-4 align-items-center">
                                        <div className="col-sm">
                                            <div>
                                                <h5 className="card-title mb-0">
                                                    <span className="me-2">{t('Total')}:</span>
                                                    <CountUp
                                                        start={0}
                                                        end={agencies?.pagination?.total || 0}
                                                        duration={1}
                                                        className="text-primary"
                                                    />
                                                </h5>
                                            </div>
                                        </div>
                                    </Row>
                                </CardHeader>
                                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                                    <Row className="g-4 align-items-center mt-0">
                                        <Col sm={12} lg={3} className='mt-3 mt-md-2'>
                                            <LabelWrapper label={t('Media')} isShow={!!mediaSearch?.value}>
                                                <DropdownMedia
                                                    name="medias"
                                                    dataList={listMedia}
                                                    initialValue={mediaSearch}
                                                    onChangeSelect={(event: any) => {
                                                        setMediaSearch((_prev) => event);
                                                    }}
                                                />
                                            </LabelWrapper>
                                        </Col>
                                        <Col sm={12} lg={2} className='mt-3 mt-md-2'>
                                            <LabelWrapper label={t('Device')} isShow={!!deviceSearch?.value}>
                                                <DropdownOption
                                                    name=""
                                                    dataList={DEVICE_OPTIONS_LANG}
                                                    placeholder={`${t("Device")}...`}
                                                    className="search-filter-category-type dropdown-status-rounded"
                                                    classNamePrefix="name-prefix"
                                                    initialValue={deviceSearch || null}
                                                    onChangeSelect={(e: any) => setDeviceSearch(e)}
                                                    isHasOptionAll={true}
                                                    optionAll={{ label: t('All Device'), value: '' }}
                                                />
                                            </LabelWrapper>
                                        </Col>
                                        <Col sm={12} lg={3} className='date-picker-wrapper-custom mt-3 mt-md-2'>
                                            <LabelWrapper label={t('Date')} isShow={!!dateSearch}>
                                                <DatePickerCustom
                                                    placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                                    startDate={startDate || null}
                                                    endDate={endDate || null}
                                                    onChangePicker={handleChangePicker}
                                                    showOptions={[
                                                        'today', 'yesterday', 'two_day_ago',
                                                        'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                                                        'this_week', 'last_week',
                                                        'this_month', 'last_month',
                                                    ]}
                                                />
                                            </LabelWrapper>
                                        </Col>
                                        <Col sm={12} lg={4} className="hstack gap-1 justify-content-sm-center justify-content-md-between mt-3 mt-md-2">
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary me-2 "
                                                    onClick={searchData}
                                                    disabled={isAgencyLoading}
                                                >
                                                    <i className="ri-search-line align-bottom me-1"></i>{" "}
                                                    {t('Button Search')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary fs-14"
                                                    onClick={resetData}
                                                >
                                                    <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                    {t('Button Reset')}
                                                </button>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                                <ChartLine
                                    className="my-3"
                                    titles={[t('Impression'), t('Revenue')]}
                                    categories={categories}
                                    series={series}
                                />
                                <CardBody>
                                    <TableContainer
                                        className="custom-header-css"
                                        divClass="table-responsive table-card"
                                        tableClass="align-middle table-bordered-dashed table-have-first-row-is-total"
                                        theadClass="table-light text-muted"
                                        columns={columns}
                                        data={agencies?.list?.length ? (
                                            [
                                                {
                                                    ...agencies?.total_data,
                                                    domain: null,
                                                    text_row: t('Sum')
                                                },
                                                ...agencies?.list]
                                        ) : []}
                                        customPageSize={query.limit}
                                        customPageIndex={query.page - 1}
                                        totalRecords={agencies?.pagination?.total}
                                        customPageCount={Math.ceil(Number(agencies?.pagination?.total) / Number(agencies?.pagination?.limit))}
                                        handleChangePage={handleChangePage}
                                        manualSorting={true}
                                        sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                                        handleChangeSorting={handleChangeSorting}
                                        isLoading={isAgencyLoading}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ReportAgency;